import { useState, useEffect } from "react";
import { MapPin } from "lucide-react";
import { eventsApi } from "../../utils/api";
import { capitalize } from "../../utils/helpers";

// One in-flight/settled fetch shared by every instance: the configured scan cities are
// near-static, and a page can now mount several of these (the venues page renders two,
// three with the edit modal open). Cached for the session — a city activated or
// deactivated elsewhere in the app shows up after a reload.
let citiesPromise;
// Exported so a page that needs the same list without this dropdown (the artists list
// renders it as a column filter instead) shares the one cached fetch.
export const loadCities = () => (citiesPromise ??= eventsApi.getScanCities().catch(() => []));

/**
 * City dropdown populated from the configured scan cities.
 *
 * Props:
 *   value        – currently selected city string (empty string = all)
 *   onChange     – called with the new city string on selection
 *   allLabel     – label for the empty-string option (default "All cities")
 *   extraOptions – extra [{value, label}] entries appended after the cities
 *                  (e.g. an "Unassigned" sentinel filter)
 *   widthClass   – Tailwind width for the select (default "w-36"); widen it when
 *                  `allLabel` is longer than the default, or it truncates
 */
export function CitySelect({ value, onChange, allLabel = "All cities", extraOptions = [], widthClass = "w-36" }) {
  const [cities, setCities] = useState([]);

  useEffect(() => {
    let cancelled = false;
    loadCities().then((data) => {
      if (!cancelled) setCities(data);
    });
    return () => { cancelled = true; };
  }, []);

  return (
    <div className="relative">
      <MapPin className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground pointer-events-none" />
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        // The browser's own arrow, with `pr-8` reserving room for it: `appearance-none` left this
        // with no dropdown affordance at all in Chrome, and suppressing it is not reliable
        // anyway (see the Picker note in pages/settings/TicketProviders.jsx).
        className={`${widthClass} h-9 rounded-md border border-input bg-background pl-8 pr-8 text-sm focus:outline-none focus:ring-2 focus:ring-ring cursor-pointer ${!value ? "text-muted-foreground" : ""}`}
      >
        <option value="">{allLabel}</option>
        {/* A stored value whose city was since deactivated is not in `cities`; without its own
            option the browser silently renders the first one instead, showing a value that is
            not the one held. */}
        {value && !cities.includes(value) && !extraOptions.some((o) => o.value === value) && (
          <option value={value}>{capitalize(value)}</option>
        )}
        {cities.map((city) => (
          <option key={city} value={city}>{capitalize(city)}</option>
        ))}
        {extraOptions.map((opt) => (
          <option key={opt.value} value={opt.value}>{opt.label}</option>
        ))}
      </select>
    </div>
  );
}
